import React, { Component, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { Link, type RouteComponentProps } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import { bindActionCreators } from "redux";
import { GetEditMerchantGroupViewPath } from "./EditMerchantGroupView";
import MerchantGroupsView, {
  MERCHANT_GROUPS_VIEW_PATH,
} from "./MerchantGroupsView";
import { ContentDetails } from "src/app/ui/authenticated/ContentDetails";
import { GetMerchantGroupByMerchantGroupUUID } from "src/merchantgroups/actions/MerchantGroupActionCreators";
import {
  MerchantGroupEdit,
  MerchantGroupLogo,
  MerchantGroupName,
  MerchantGroupStatus,
} from "src/merchantgroups/formatters/MerchantGroupFormatters";
import type IMerchantGroupURLParams from "src/merchantgroups/objects/IMerchantGroupURLParams";
import type MerchantGroup from "src/merchantgroups/objects/MerchantGroup";
import Loader from "src/shared/components/Loader";
import { UUID } from "src/shared/components/UUID";
import { ContactDetails } from "src/shared/contacts/components/ContactDetails";
import FormattedInstant from "src/shared/formatters/FormattedInstant";
import { Formatter } from "src/shared/formatters/Formatter";

interface IMerchantGroupDetailViewInnerProps
  extends IMerchantGroupDetailViewWithActionCreatorsProps {
  GetMerchantGroupByMerchantGroupUUID: (
    merchantGroupUUID: string,
  ) => Promise<MerchantGroup>;
}

interface IMerchantGroupDetailViewInnerState {
  promise?: Promise<MerchantGroup>;
}

class MerchantGroupDetailViewInner extends Component<
  IMerchantGroupDetailViewInnerProps,
  IMerchantGroupDetailViewInnerState
> {
  public state = {
    promise: undefined,
  };

  public constructor(props: IMerchantGroupDetailViewInnerProps) {
    super(props);

    this.onLoad = this.onLoad.bind(this);
  }

  public componentDidMount(): void {
    this.onLoadMerchantGroup();
  }

  public onLoadMerchantGroup(): void {
    const {
      match: {
        params: { merchantGroupUUID },
      },
    } = this.props;
    this.setState({
      promise:
        this.props.GetMerchantGroupByMerchantGroupUUID(merchantGroupUUID),
    });
  }

  public onLoad(): void {
    // Reload Main List.
    const { onLoad } = this.props;
    onLoad();

    // Reload Merchant Group.
    this.onLoadMerchantGroup();
  }

  public render(): ReactNode {
    const { promise } = this.state;

    if (promise === undefined) {
      return null;
    }

    const { children } = this.props;

    return (
      <ContentDetails dismissPath={MERCHANT_GROUPS_VIEW_PATH}>
        <Loader promise={promise}>
          {(merchantGroup: MerchantGroup) => (
            <>
              {children && children(merchantGroup, this.onLoad)}

              <Row className="my-4">
                <Col sm={6}>
                  <UUID>{merchantGroup.merchantGroupUUID}</UUID>
                </Col>
                <Col sm={6} className="text-right">
                  <small>
                    <MerchantGroupStatus>{merchantGroup}</MerchantGroupStatus>
                  </small>
                </Col>
              </Row>

              <Row className="my-4">
                <Col sm={12} className="content-details-branding">
                  <div className="content-details-branding-logo">
                    <MerchantGroupLogo size={3}>
                      {merchantGroup}
                    </MerchantGroupLogo>
                  </div>
                  <div className="content-details-branding-details">
                    <h3>
                      <MerchantGroupName>{merchantGroup}</MerchantGroupName>
                    </h3>
                    <p>
                      <FormattedMessage id="merchant_groups.name" />
                    </p>
                  </div>
                </Col>
              </Row>

              <hr />

              <ContactDetails
                contact={merchantGroup}
                className="my-4"
                bottomSection={
                  <>
                    <Col sm={4} tag="dt">
                      <FormattedMessage id="contacts.created" />
                    </Col>
                    <Col sm={8} tag="dd">
                      <FormattedInstant value={merchantGroup.firstCreated} />
                    </Col>

                    <Col sm={4} tag="dt">
                      <FormattedMessage id="contacts.last_updated" />
                    </Col>
                    <Col sm={8} tag="dd">
                      <FormattedInstant value={merchantGroup.lastUpdated} />
                    </Col>
                  </>
                }
              >
                <Col sm={4} tag="dt">
                  <FormattedMessage id="merchantGroups.isAgency" />
                </Col>
                <Col sm={8} tag="dd">
                  <Formatter>{merchantGroup.isAgency}</Formatter>
                </Col>
              </ContactDetails>

              <Row className="my-4">
                <Col sm={12}>
                  <Link to={GetEditMerchantGroupViewPath(merchantGroup)}>
                    <Button color="info" className="w-100">
                      <MerchantGroupEdit>{merchantGroup}</MerchantGroupEdit>
                    </Button>
                  </Link>
                </Col>
              </Row>
            </>
          )}
        </Loader>
      </ContentDetails>
    );
  }
}

// With Redux
export interface IMerchantGroupDetailViewWithActionCreatorsProps
  extends IMerchantGroupDetailViewProps {
  onLoad: () => void;
}
export const MerchantGroupDetailViewWithActionCreators = connect(
  () => {
    return {};
  },
  (dispatch) =>
    bindActionCreators(
      {
        GetMerchantGroupByMerchantGroupUUID,
      },
      dispatch,
    ),
)(MerchantGroupDetailViewInner);

// Export
export interface IMerchantGroupDetailViewProps
  extends RouteComponentProps<IMerchantGroupURLParams> {
  children?: (merchantGroup: MerchantGroup, onLoad: () => void) => ReactNode;
}
export default (props: IMerchantGroupDetailViewProps) => {
  return (
    <MerchantGroupsView {...props}>
      {(merchantGroups: MerchantGroup[], onLoad: () => void) =>
        React.createElement(MerchantGroupDetailViewWithActionCreators, {
          ...props,
          onLoad,
        })
      }
    </MerchantGroupsView>
  );
};
